// Microphone capture. Owns the MediaStream, a MediaRecorder for the answer
// audio, and an AnalyserNode driving both the live waveform and voice-activity
// detection (VAD). A live mic left open is a bug — release() stops every track.

export interface RecorderStop {
  blob: Blob;
  mime: string;
  duration_s: number;
  spoke: boolean; // did we ever detect speech?
}

const SPEECH_RMS = 0.018; // above this = speech
const BAR_COUNT = 9;

export class Recorder {
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mime = 'audio/webm';
  private startedAt = 0;
  private spoke = false;
  private lastSpeechAt = 0;
  private raf = 0;
  private timeBuf: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(0));
  private bars: number[] = new Array(BAR_COUNT).fill(0.12);

  /** Acquire the mic once. Throws on denial/unavailability — caller shows the state. */
  async acquire(): Promise<void> {
    if (this.stream) return;
    if (!navigator.mediaDevices?.getUserMedia) throw new DOMException('unsupported', 'NotSupportedError');
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    try {
      this.ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const src = this.ctx.createMediaStreamSource(this.stream);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 1024;
      this.analyser.smoothingTimeConstant = 0.75;
      src.connect(this.analyser);
      this.timeBuf = new Uint8Array(new ArrayBuffer(this.analyser.fftSize));
    } catch (e) {
      // Building the audio graph failed — don't leave the mic stream open.
      this.release();
      throw e;
    }
  }

  hasStream(): boolean {
    return !!this.stream;
  }

  private pickMime(): string {
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    for (const c of cands) if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
    return '';
  }

  /**
   * Begin recording. `onSilence` fires once after `silenceMs` of continuous
   * silence *following* detected speech (VAD auto-advance). `onMaxReached`
   * fires once at `maxMs` (the examiner moving on). Both are optional.
   */
  start(opts: { silenceMs?: number; maxMs?: number; onSilence?: () => void; onMaxReached?: () => void } = {}): void {
    if (!this.stream) throw new Error('mic not acquired');
    if (this.ctx?.state === 'suspended') void this.ctx.resume();
    this.chunks = [];
    this.spoke = false;
    this.startedAt = performance.now();
    this.lastSpeechAt = this.startedAt;
    const mime = this.pickMime();
    this.mime = mime || 'audio/webm';
    this.rec = mime ? new MediaRecorder(this.stream, { mimeType: mime }) : new MediaRecorder(this.stream);
    this.rec.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.rec.start(200);

    let silenceFired = false;
    let maxFired = false;
    const tick = () => {
      if (!this.analyser) return;
      this.analyser.getByteTimeDomainData(this.timeBuf);
      // RMS around the 128 zero point.
      let sum = 0;
      for (let i = 0; i < this.timeBuf.length; i++) {
        const v = (this.timeBuf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / this.timeBuf.length);
      const now = performance.now();
      if (rms > SPEECH_RMS) {
        this.spoke = true;
        this.lastSpeechAt = now;
      }
      this.updateBars(rms);

      if (opts.maxMs && !maxFired && now - this.startedAt >= opts.maxMs) {
        maxFired = true;
        opts.onMaxReached?.();
      }
      if (opts.silenceMs && !silenceFired && this.spoke && now - this.lastSpeechAt >= opts.silenceMs) {
        silenceFired = true;
        opts.onSilence?.();
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private updateBars(rms: number): void {
    // Map RMS to a lively 9-bar spread, jittered per bar for organic motion.
    const base = Math.min(1, rms * 7);
    for (let i = 0; i < BAR_COUNT; i++) {
      const center = 1 - Math.abs(i - (BAR_COUNT - 1) / 2) / ((BAR_COUNT - 1) / 2); // 0..1 peak in middle
      const target = 0.12 + base * (0.4 + 0.6 * center) * (0.7 + 0.3 * Math.sin(performance.now() / 90 + i));
      this.bars[i] = this.bars[i] * 0.6 + Math.max(0.12, Math.min(1, target)) * 0.4;
    }
  }

  /** Live bar heights (0..1) for the waveform. */
  levels(): number[] {
    return this.bars;
  }

  async stop(): Promise<RecorderStop> {
    cancelAnimationFrame(this.raf);
    const duration_s = (performance.now() - this.startedAt) / 1000;
    const spoke = this.spoke;
    const rec = this.rec;
    this.rec = null;
    const blob = await new Promise<Blob>((resolve) => {
      if (!rec || rec.state === 'inactive') {
        resolve(new Blob(this.chunks, { type: this.mime }));
        return;
      }
      rec.onstop = () => resolve(new Blob(this.chunks, { type: this.mime }));
      rec.stop();
    });
    return { blob, mime: this.mime, duration_s, spoke };
  }

  /** Stop every track and tear down the audio graph — no lingering mic. */
  release(): void {
    cancelAnimationFrame(this.raf);
    try {
      this.rec?.state !== 'inactive' && this.rec?.stop();
    } catch {
      /* ignore */
    }
    this.rec = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.analyser = null;
    if (this.ctx && this.ctx.state !== 'closed') void this.ctx.close();
    this.ctx = null;
  }
}
